import classNames from "classnames"
import React, { useRef, useMemo, useState, useEffect } from "react"

import { Color } from "../../../constants/V2/color"
import { isTablet, useIsBreakpoint } from "../../../utils/V2/screen"
import PillButton from "../../elements/V2/Buttons/PillButton"
import GatsbyStoryblokImage from "../../elements/V2/GatsbyStoryblokImage"
import CircleCheckIcon from "../../elements/V2/Icons/CircleCheckIcon"
import CircleCrossIcon from "../../elements/V2/Icons/CircleCrossIcon"
import Logo from "../../elements/V2/Icons/YocoLogoIcon"
import Tooltip from "../../elements/V2/Tooltip"
import Typography from "../../elements/V2/Typography"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import AccordionArrowIcon from "@components/elements/V2/Icons/AccordionArrowIcon"
import CircleDashIcon from "@components/elements/V2/Icons/CircleDashIcon"
import { TableRegex } from "@constants/tables"
import { useGetProductBySlug } from "@hooks/V2/storyblok/useProduct"
import { checkPromotion } from "@utils/shop"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

const CELL_CLASSES = "text-center flex items-center px-16 py-20 rounded-10"
const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] text-start"
type Props = {
  columnImages?: string[]
  rowHeadings: string[]
  data: string[][]
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  outerContainer: "gridSection" | "tabSelector"
}

const DoubleEntryComparisonTable = ({
  columnImages,
  rowHeadings,
  data,
  backgroundColor,
  outerContainer,
}: Props) => {
  const tableTheme =
    backgroundColor === Color.Black || backgroundColor === Color.Charcoal
      ? "dark"
      : "light"
  const getProductBySlug = useGetProductBySlug()
  const titleRowIndices = useRef<number[]>([])

  const columnCount = useMemo(() => {
    let longestDataRow = 0
    data.forEach((dataRow) => {
      if (dataRow.length > longestDataRow) {
        longestDataRow = dataRow.length
      }
    })
    return longestDataRow
  }, [data])

  const maxDesktopColumns = Math.min(columnCount, 3)
  const maxMobileColumns = 2
  const isMobileView = useIsBreakpoint("mobile")

  const initialSelectedProducts = useMemo(() => {
    const availableProducts = Array.from({ length: columnCount }, (_, i) => i)
    return availableProducts.slice(
      0,
      isMobileView ? maxMobileColumns : maxDesktopColumns
    )
  }, [columnCount, maxDesktopColumns, isMobileView])

  const [selectedProducts, setSelectedProducts] = useState<number[]>(
    initialSelectedProducts
  )

  useEffect(() => {
    const availableProducts = Array.from({ length: columnCount }, (_, i) => i)
    setSelectedProducts(
      availableProducts.slice(0, isMobileView ? 2 : maxDesktopColumns)
    )
  }, [columnCount, maxDesktopColumns, isMobileView])
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  )

  // Get products that are not currently selected (for dropdown options)
  const availableProducts = useMemo(() => {
    return Array.from({ length: columnCount }, (_, i) => i).filter(
      (productIndex) => !selectedProducts.includes(productIndex)
    )
  }, [columnCount, selectedProducts])

  const toggleDropdown = (columnIndex: number) => {
    setOpenDropdownIndex(openDropdownIndex === columnIndex ? null : columnIndex)
  }

  const selectProduct = (
    newProductIndex: number,
    replacingColumnIndex: number
  ) => {
    setSelectedProducts((prev) => {
      const newSelected = [...prev]
      const currentProductIndex = newSelected[replacingColumnIndex]

      newSelected[replacingColumnIndex] = newProductIndex

      const duplicateIndex = newSelected.findIndex(
        (p, i) => p === newProductIndex && i !== replacingColumnIndex
      )
      if (duplicateIndex !== -1) {
        newSelected[duplicateIndex] = currentProductIndex
      }

      return newSelected
    })
    setOpenDropdownIndex(null)
  }

  const RenderCellData = (
    rowIndex: number,
    columnIndex: number,
    displayIndex: number
  ) => {
    const cellData = data[rowIndex][columnIndex]
    const isCheckIcon = cellData.match(TableRegex.CHECK_ICON)
    const isCrossIcon = cellData.match(TableRegex.CROSS_ICON)
    const isCheckIconBlackStroke = cellData.match(
      TableRegex.CHECK_ICON_BLACK_STROKE
    )
    const isDashIcon = cellData.match(TableRegex.DASH_ICON)
    const isList = cellData.match(TableRegex.LIST)
    const isPriceFromStoryblokSlug = cellData.match(
      TableRegex.PRICE_FROM_STORYBLOK_SLUG
    )
    const isDetailedPriceFromStoryblokSlug = cellData.match(
      TableRegex.DETAILED_PRICE_FROM_STORYBLOK_SLUG
    )
    const isPrice = cellData.match(TableRegex.PRICE)
    const isButton = cellData.match(TableRegex.BUTTON)
    const isHeader = cellData.match(TableRegex.HEADER)
    const isProduct = cellData.match(TableRegex.PRODUCT)
    const isBullet = cellData.match(TableRegex.BULLET)
    let productPrice = 0
    const parseContent = (
      regexMatch: RegExpMatchArray | null,
      prefixLength: number
    ) =>
      regexMatch
        ? regexMatch[0]
            .slice(prefixLength, -2)
            .split(",")
            .map((item) => item.trim())
        : []

    const bulletPoints = parseContent(isBullet, 8)
    const list = parseContent(isList, 6)
    let priceSubScript
    let priceSideScript
    let promotionStartDate
    let promotionEndDate
    let promotionSavings = 0

    if (isPriceFromStoryblokSlug || isDetailedPriceFromStoryblokSlug) {
      const slug =
        isDetailedPriceFromStoryblokSlug?.[1] ||
        isPriceFromStoryblokSlug?.[1] ||
        ""

      const product = getProductBySlug(slug)

      if (product) {
        productPrice = Number(product.price)
        if (isDetailedPriceFromStoryblokSlug) {
          // TODO: fix casing difference between bundles and products
          "priceSubScript" in product
            ? (priceSubScript = product.priceSubScript)
            : null
          "priceSubscript" in product
            ? (priceSubScript = product.priceSubscript)
            : null
          // TODO: fix casing difference between bundles and products
          "priceSideScript" in product
            ? (priceSideScript = product.priceSideScript)
            : null
          "priceSidescript" in product
            ? (priceSideScript = product.priceSidescript)
            : null
          "savingsAmount" in product
            ? (promotionSavings = Number(product.savingsAmount))
            : null
          "promotionStartDate" in product
            ? (promotionStartDate = product.promotionStartDate)
            : null
          "promotionEndDate" in product
            ? (promotionEndDate = product.promotionEndDate)
            : null
        }
      }
    }
    const showPromotion = checkPromotion(promotionStartDate, promotionEndDate)
    let buttonLabel, buttonLink, secondButtonLabel, secondButtonLink

    if (isButton) {
      const buttonMatch = cellData.match(TableRegex.BUTTON)
      if (buttonMatch) {
        const [, firstLabel, firstLink, secondLabel, secondLink] = buttonMatch

        buttonLabel = firstLabel
        buttonLink = firstLink
        secondButtonLabel = secondLabel
        secondButtonLink = secondLink
      }
    }

    return (
      <div
        key={`column-${columnIndex}-row-${rowIndex}`}
        className={classNames(
          CELL_CLASSES,
          "flex !items-start",
          {
            "hidden sm:flex":
              displayIndex >= maxMobileColumns &&
              displayIndex < maxDesktopColumns,
            hidden: displayIndex >= maxDesktopColumns,
          },
          isCheckIcon || isCrossIcon
            ? "!pr-auto w-full flex-row-reverse !justify-end gap-8 sm:gap-32"
            : "flex-col",
          {
            "sm:!rounded-r-none": displayIndex === 0,
            "rounded-l-none": displayIndex === 1,
            "sm:!rounded-l-none": displayIndex === maxDesktopColumns - 1,
            "sm:!rounded-none":
              displayIndex > 0 && displayIndex < maxDesktopColumns - 1,
            "rounded-r-none sm:rounded-r-10": displayIndex === 0,
            "rounded-l-none sm:rounded-l-10":
              displayIndex === maxDesktopColumns - 1,
            "mt-16 md:mt-24": titleRowIndices.current.find(
              (titleRowIndex) => titleRowIndex === rowIndex
            ),
            "justify-center":
              isPriceFromStoryblokSlug ||
              isDetailedPriceFromStoryblokSlug ||
              isPrice,
          },
          rowIndex === data.length - 1 && tableTheme === "light"
            ? "bg-white"
            : rowIndex === data.length - 1 && tableTheme === "dark"
              ? backgroundColor === Color.Black
                ? "!bg-black"
                : "!bg-charcoal"
              : "",
          rowIndex % 2 === 0
            ? tableTheme === "light"
              ? "bg-white"
              : "bg-dark-gray"
            : tableTheme === "light"
              ? "bg-light-gray"
              : backgroundColor === Color.Black
                ? "bg-charcoal"
                : "bg-black",
          { "!py-0": isButton }
        )}
      >
        <Tooltip
          text={
            rowHeadings[columnIndex].match(TableRegex.TITLE)
              ? rowHeadings[columnIndex]
                  .replace(":title(", "")
                  .replace("):", "")
              : rowHeadings[columnIndex]
          }
          backgroundColor={
            tableTheme === "light"
              ? backgroundColor === Color.Charcoal
                ? Color.Black
                : Color.Charcoal
              : Color.White
          }
          showTooltipCondition="hoverContentEllipsed"
        >
          <Typography
            font="grotesk"
            weight="book"
            color={getAccentColor(
              tableTheme === "light" ? Color.White : Color.Charcoal
            )}
            size="body-sm-fixed"
            text={
              rowHeadings[rowIndex].match(TableRegex.TITLE)
                ? ""
                : rowHeadings[rowIndex]
            }
            className={classNames(
              {
                "!hidden":
                  isPriceFromStoryblokSlug ||
                  isDetailedPriceFromStoryblokSlug ||
                  isPrice,
                "pb-10": !(isCheckIcon || isCrossIcon),
                "mt-4": isCheckIcon || isCrossIcon,
              },
              CELL_TEXT_CLASSSES,
              "text-left opacity-70"
            )}
          />
        </Tooltip>
        {isCrossIcon ? (
          <div
            className={classNames(
              "flex items-center",
              isCrossIcon?.[1] && "w-full justify-self-start"
            )}
          >
            <CircleCrossIcon
              className={classNames(
                "shrink-0",
                tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
              )}
            />
            {isCrossIcon?.[1] && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size={isTablet() ? "subscript-sm" : "body-md"}
                text={isCrossIcon?.[1]}
                className="pl-16 text-start sm:pl-[2px] md:pl-10"
              />
            )}
          </div>
        ) : isCheckIcon ? (
          <div
            className={classNames(
              "flex h-full",
              isCheckIcon?.[2] && "w-full justify-start"
            )}
          >
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                isCheckIcon?.[1] === "yellow"
                  ? "fill-yellow"
                  : isCheckIcon?.[1] === "orange"
                    ? "fill-orange"
                    : isCheckIcon?.[1] === "blue"
                      ? "fill-blue"
                      : isCheckIcon?.[1] === "white"
                        ? "fill-white"
                        : undefined
              )}
              checkmarkClassName={
                isCheckIcon?.[1] === "white" ? "stroke-blue" : "stroke-white"
              }
            />
            {isCheckIcon?.[2] && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size={isTablet() ? "subscript-sm" : "body-md"}
                text={isCheckIcon?.[2]}
                className="pl-16 text-start sm:pl-[2px] md:pl-10"
              />
            )}
          </div>
        ) : isCheckIconBlackStroke ? (
          <div
            className={classNames(
              "flex items-center",
              isCheckIconBlackStroke?.[2] && "w-full justify-start"
            )}
          >
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                isCheckIconBlackStroke?.[1] === "yellow"
                  ? "fill-yellow"
                  : isCheckIconBlackStroke?.[1] === "orange"
                    ? "fill-orange"
                    : isCheckIconBlackStroke?.[1] === "blue"
                      ? "fill-blue"
                      : isCheckIconBlackStroke?.[1] === "white"
                        ? "fill-white"
                        : undefined
              )}
              checkmarkClassName={
                isCheckIconBlackStroke?.[1] === "white"
                  ? "stroke-black"
                  : "stroke-white"
              }
            />
            {isCheckIconBlackStroke?.[2] && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size={isTablet() ? "subscript-sm" : "body-md"}
                text={isCheckIconBlackStroke?.[2]}
                className="pl-16 text-start sm:pl-[2px] md:pl-10"
              />
            )}
          </div>
        ) : isDashIcon ? (
          <div className="flex items-center">
            <CircleDashIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={
                isDashIcon?.[1] === "yellow"
                  ? "fill-yellow"
                  : isDashIcon?.[1] === "orange"
                    ? "fill-orange"
                    : isDashIcon?.[1] === "blue"
                      ? "fill-blue"
                      : isDashIcon?.[1] === "white"
                        ? "fill-white"
                        : undefined
              }
              dashClassName={
                isDashIcon?.[1] === "white" ? "stroke-black" : "stroke-white"
              }
            />
          </div>
        ) : isHeader ? (
          <Typography
            text={cellData.replace(":header(", "").replace("):", "")}
            size="body-lg"
            font="grotesk"
            weight="medium"
            color={getAccentColor(
              tableTheme === "light" ? Color.White : Color.Charcoal
            )}
          />
        ) : cellData.match(/:yocoLogo:/) ? (
          <Logo
            className={classNames(
              "max-h-32",
              tableTheme === "light" ? "fill-black" : "fill-white"
            )}
            alt="Yoco Logo"
          />
        ) : cellData.match(/:crossIcon:/) ? (
          <CircleCrossIcon
            className={
              tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
            }
          />
        ) : isList && list ? (
          <div className="flex flex-col gap-0.5 text-start">
            {list.map((listItem, index) => (
              <div className="flex gap-2" key={index}>
                <div>
                  <CircleCheckIcon
                    width={16}
                    height={16}
                    className={
                      tableTheme === "light" ? "text-charcoal" : "text-white"
                    }
                  />
                </div>
                <Typography
                  text={listItem}
                  size="body-md"
                  font="grotesk"
                  weight="book"
                  color={getAccentColor(
                    tableTheme === "light" ? Color.White : Color.Charcoal
                  )}
                />
              </div>
            ))}
          </div>
        ) : isBullet && bulletPoints ? (
          <div className="flex flex-col gap-0.5 text-start">
            {bulletPoints.map((bulletPoint, index) => (
              <Typography
                text={`• ${bulletPoint}`}
                size="body-md"
                font="grotesk"
                weight="book"
                color={getAccentColor(
                  tableTheme === "light" ? Color.White : Color.Charcoal
                )}
                key={index}
              />
            ))}
          </div>
        ) : isPriceFromStoryblokSlug ? (
          <Typography
            font="grotesk"
            weight="medium"
            color={tableTheme === "light" ? Color.Charcoal : Color.White}
            size="lead-lg"
            text={"R" + productPrice}
            className={CELL_TEXT_CLASSSES}
          />
        ) : isDetailedPriceFromStoryblokSlug ? (
          <div className="flex flex-col items-start gap-8">
            {((showPromotion && promotionSavings > 0) ||
              isDetailedPriceFromStoryblokSlug?.[2]) && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size="body-sm"
                text={
                  showPromotion && promotionSavings > 0
                    ? "Save R" + promotionSavings
                    : isDetailedPriceFromStoryblokSlug?.[2]
                }
                className={classNames(CELL_TEXT_CLASSSES, "opacity-70")}
                leadingNone
              />
            )}
            <div className="flex place-items-baseline gap-2">
              <Typography
                font="grotesk"
                weight="medium"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size="lead-lg"
                text={"R" + productPrice}
                className={CELL_TEXT_CLASSSES}
                leadingNone
              />
              {priceSideScript && (
                <Typography
                  font="grotesk"
                  weight="medium"
                  color={tableTheme === "light" ? Color.Charcoal : Color.White}
                  size="subscript-sm"
                  text={priceSideScript}
                  className={CELL_TEXT_CLASSSES}
                  leadingNone
                />
              )}
            </div>
            {priceSubScript && (
              <Typography
                font="grotesk"
                weight="book"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
                size="body-sm"
                text={priceSubScript}
                className={CELL_TEXT_CLASSSES}
                leadingNone
              />
            )}
          </div>
        ) : isButton && buttonLabel && buttonLink ? (
          <div className="flex w-full flex-col items-center gap-16 sm:flex-row">
            <PillButton
              size="medium"
              style="solid"
              text={buttonLabel}
              color={Color.Blue}
              linkUrl={buttonLink}
              trackingEvent="TableButtonEvent"
              trackingEventKey="column"
              trackingEventValue={data[0][columnIndex]}
              className={
                secondButtonLabel && secondButtonLink
                  ? "sm:!w-8/12"
                  : "sm:!w-10/12"
              }
            />
            {secondButtonLabel && secondButtonLink && (
              <TextButton
                text={secondButtonLabel}
                linkUrl={secondButtonLink}
                style="text"
                color={tableTheme === "light" ? Color.Charcoal : Color.White}
              />
            )}
          </div>
        ) : (
          !isProduct && (
            <>
              <Tooltip
                text={
                  isPrice
                    ? cellData.replace(":price(", "").replace("):", "")
                    : cellData
                }
                backgroundColor={
                  tableTheme === "light" ? Color.Charcoal : Color.White
                }
                showTooltipCondition="hoverContentEllipsed"
              >
                <Typography
                  font="grotesk"
                  weight={isPrice || rowIndex === 0 ? "medium" : "book"}
                  color={tableTheme === "light" ? Color.Charcoal : Color.White}
                  size={
                    isPrice ? "lead-lg" : rowIndex === 0 ? "body-lg" : "body-md"
                  }
                  text={
                    isPrice
                      ? cellData.replace(":price(", "").replace("):", "")
                      : cellData
                  }
                  className={CELL_TEXT_CLASSSES}
                />
              </Tooltip>
            </>
          )
        )}
      </div>
    )
  }

  const renderHeaders = () => {
    const tableColumns: React.JSX.Element[] = []
    selectedProducts.forEach((productIndex, displayIndex) => {
      const columnHeading = data[0][productIndex] || ""
      const isProduct = columnHeading.match(TableRegex.PRODUCT)
      const isOpen = openDropdownIndex === displayIndex
      const hasAvailableProducts = availableProducts.length > 0

      tableColumns.push(
        <div
          key={`${columnHeading.replace(" ", "-")}-${productIndex}`}
          className={classNames({
            "cursor-pointer": hasAvailableProducts,
            "hidden sm:block":
              displayIndex >= maxMobileColumns &&
              displayIndex < maxDesktopColumns,
            hidden: displayIndex >= maxDesktopColumns,
          })}
          onClick={() => {
            if (hasAvailableProducts) {
              toggleDropdown(displayIndex)
            }
          }}
        >
          <div
            className={classNames(
              "flex items-center gap-20 rounded-10 border-charcoal/10 py-4",
              {
                "sm:border": hasAvailableProducts,
                "h-40": !columnImages || columnImages.length === 0,
                "h-full min-h-[55px] sm:min-h-[72px]":
                  columnImages && columnImages.length > 0,
                "rounded-b-none !border-b-0": isOpen && hasAvailableProducts,
                "shadow-lg": isOpen,
              }
            )}
          >
            {columnImages && columnImages[productIndex] && (
              <div className="ml-[14px] aspect-square h-32 w-32 sm:h-64 sm:w-64">
                <GatsbyStoryblokImage
                  aspectRatio="fullWidth"
                  image={columnImages[productIndex]
                    .replace(":image(", "")
                    .replace("):", "")}
                  alt={columnHeading}
                  className="h-32 w-32 sm:h-64 sm:w-64"
                  testId="comparison-table-image"
                  imageObjectFit="contain"
                />
              </div>
            )}
            <div className="flex flex-col gap-[3px]">
              <Tooltip
                text={columnHeading}
                backgroundColor={
                  tableTheme === "light" ? Color.Charcoal : Color.White
                }
                showTooltipCondition="hoverContentEllipsed"
              >
                <Typography
                  font="grotesk"
                  weight="medium"
                  color={tableTheme === "light" ? Color.Charcoal : Color.White}
                  size={"body-lg"}
                  text={isProduct ? isProduct[1] : ""}
                  className={CELL_TEXT_CLASSSES}
                />
              </Tooltip>
              {isProduct && isProduct[2] && (
                <Tooltip
                  text={columnHeading}
                  backgroundColor={
                    tableTheme === "light" ? Color.Charcoal : Color.White
                  }
                  showTooltipCondition="hoverContentEllipsed"
                >
                  <Typography
                    font="grotesk"
                    weight="book"
                    color={
                      tableTheme === "light" ? Color.Charcoal : Color.White
                    }
                    size="body-sm"
                    text={isProduct[2]}
                    className={classNames(
                      "!hidden sm:![display:-webkit-box]",
                      CELL_TEXT_CLASSSES
                    )}
                  />
                </Tooltip>
              )}
            </div>
            {hasAvailableProducts && (
              <div className="ml-auto pr-8">
                <AccordionArrowIcon
                  open={isOpen}
                  color={getAccentColor(
                    tableTheme === "dark"
                      ? backgroundColor === Color.Black
                        ? Color.Black
                        : Color.Charcoal
                      : Color.White
                  )}
                />
              </div>
            )}
          </div>
          {isOpen && hasAvailableProducts && (
            <div
              className={classNames(
                "absolute z-20 -mt-0.5 flex w-1/2 flex-col rounded-b-10 !border-t-0 shadow-lg sm:w-[calc(33.333%-10.667px)] sm:border md:w-[calc(33.333%-21.333px)]",
                tableTheme === "light"
                  ? "border-charcoal/10 bg-white"
                  : "border-charcoal bg-black",
                {
                  "left-0": displayIndex === 0,
                  "right-0": displayIndex === selectedProducts.length - 1,
                  "left-1/2 sm:-translate-x-1/2":
                    displayIndex > 0 &&
                    displayIndex < selectedProducts.length - 1,
                  "!w-1/2": selectedProducts.length === 2,
                }
              )}
            >
              {availableProducts.map((availableProductIndex, index) => {
                const item = data[0][availableProductIndex]
                return (
                  <button
                    key={item}
                    onClick={() => {
                      selectProduct(availableProductIndex, displayIndex)
                    }}
                    className="flex flex-col"
                  >
                    {index === 0 && (
                      <hr
                        className={classNames(
                          "hidden w-full sm:block",
                          tableTheme === "light"
                            ? "border-charcoal/10"
                            : "border-charcoal"
                        )}
                      />
                    )}
                    <div
                      className={classNames(
                        "flex h-[72px] cursor-pointer items-center gap-16 px-8"
                      )}
                    >
                      {columnImages && columnImages[availableProductIndex] && (
                        <div className="ml-8 aspect-square h-32 w-32 sm:h-64 sm:w-64">
                          <GatsbyStoryblokImage
                            aspectRatio="fullWidth"
                            image={columnImages[availableProductIndex]
                              .replace(":image(", "")
                              .replace("):", "")}
                            alt={
                              item.match(TableRegex.PRODUCT)
                                ? item.match(TableRegex.PRODUCT)![1]
                                : ""
                            }
                            className="h-32 w-32 sm:h-64 sm:w-64 md:h-full md:w-auto"
                            testId="comparison-table-image"
                            imageObjectFit="contain"
                          />
                        </div>
                      )}
                      <Typography
                        font="grotesk"
                        weight="medium"
                        color={
                          tableTheme === "light" ? Color.Charcoal : Color.White
                        }
                        size="body-lg"
                        text={
                          item.match(TableRegex.PRODUCT)
                            ? item.match(TableRegex.PRODUCT)![1]
                            : ""
                        }
                        className={CELL_TEXT_CLASSSES}
                      />
                    </div>
                    {index !== availableProducts.length - 1 && (
                      <hr
                        className={classNames(
                          "w-full",
                          tableTheme === "light"
                            ? "border-charcoal/10"
                            : "border-charcoal"
                        )}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )
    })

    return tableColumns
  }

  return (
    <div
      className={classNames(
        "grid grid-cols-1 gap-x-16 md:gap-x-20 lg:gap-x-32"
      )}
    >
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "sticky z-10 rounded-b-10 py-16 sm:gap-x-16 md:gap-x-32",
          outerContainer === "gridSection"
            ? "top-navHeight-xs md:top-navHeight-md lg:top-navHeight-lg"
            : "top-[130px] sm:top-[130px] lg:top-[62px]",
          "grid grid-cols-2",
          columnCount < 3 ? "sm:grid-cols-2" : "sm:grid-cols-3",
          {
            "!rounded-l-none": openDropdownIndex === 0,
          }
        )}
      >
        {renderHeaders()}
      </div>

      {data.slice(1).map((_, rowIndex) => (
        <div
          key={`row-${rowIndex + 1}`}
          className={classNames(
            "z-1 grid grid-flow-row",
            `grid-cols-${Math.min(selectedProducts.length, maxMobileColumns)} sm:grid-cols-${Math.min(selectedProducts.length, maxDesktopColumns)}`
          )}
        >
          {selectedProducts.map((productIndex, displayIndex) =>
            RenderCellData(rowIndex + 1, productIndex, displayIndex)
          )}
        </div>
      ))}
    </div>
  )
}

export default DoubleEntryComparisonTable
